/*
 * Application navigation markup for public links, account controls, inbox and staff reviews.
 * The application shell supplies route links and callbacks to this shared composition.
 * Individual menus retain their own disclosure state while the shell chooses available actions.
 * Example: registerComponents(registry);
 *          <AppNavigation state={navigation} actions={actions} controls={controls} context={context}/>
 */

import { Icon } from '../../runtime/components/presentation.jsx';
import { publicLoginHTML } from '../accounts/public-login.jsx';
import { registerInboxMenu, InboxMenu } from './inbox-menu.jsx';
import { registerReviewsMenu, ReviewsMenu } from './reviews-menu.jsx';
import { registerComponents as registerAccountMenu, AccountMenu } from './account-menu.jsx';
import {
  registerComponents as registerConnection,
  ConnectionStatus
} from '../ui/connection-status.jsx';
import { SignOut } from '../accounts/sign-out.jsx';

export function registerComponents(registry) {
  registerAccountMenu(registry);
  registerConnection(registry);
  registerInboxMenu(registry);
  registerReviewsMenu(registry);
}

export function AppNavigation({ state, actions, controls, context }) {
  if (state.kind === 'loading') return null;
  return (
    <div class={`nav-pill${state.kind === 'member' ? '' : ' nav-auth-pill'}`}>
      {state.kind === 'member' ? (
        <MemberLeading state={state} actions={actions} context={context}/>
      ) : (
        <AuthLinks state={state} actions={actions} context={context}/>
      )}
      {controls}
      {state.kind === 'member' ? (
        <MemberTrailing state={state} actions={actions} context={context}/>
      ) : null}
    </div>
  );
}

function AuthLinks({ state, actions, context }) {
  if (state.kind === 'unverified')
    return (
      <>
        <a href="/me" class="nav-auth-link" aria-current="page">Account</a>
        <SignOut
          context={context}
          buttonId="unverified-logout"
          buttonClass="nav-auth-link"
          label="Log out"/>
      </>
    );
  return (
    <>
      {publicLoginHTML(state.publicLogin)}
      <a
        href="/register"
        class="nav-auth-link"
        aria-current={state.onRegister ? 'page' : null}
        onClick={actions.register}>
        Register
      </a>
    </>
  );
}

function MemberLeading({ state, actions, context }) {
  return (
    <>
      <a id="nav-home" href={state.homeHref} onClick={actions.home} class="nav-icon-button nav-home" title="Home" aria-label="Home" aria-describedby="conn-status-indicator">
        <Icon name="home" classes="icon-sm"/>
        <ConnectionStatus context={context}/>
      </a>
      <div class="nav-divider" aria-hidden="true"/>
      <InboxMenu
        context={context}
        source={state.source}
        openConversation={state.openConversation}
        leftConversation={state.leftConversation}
        startChat={state.startChat}/>
      <ReviewsMenu context={context} source={state.source}/>
    </>
  );
}

function MemberTrailing({ state, actions, context }) {
  return (
    <>
      <div class="nav-divider" data-nav-account-divider="" aria-hidden="true"/>
      {state.isAdmin ? (
        <a href="/admin" onClick={actions.admin} class="nav-icon-button" title="Admin" aria-label="Admin">
          <Icon name="crown" classes="icon-sm"/>
        </a>
      ) : null}
      <AccountMenu
        context={context}
        username={state.accountLabel}
        email={state.accountEmail}
        destination={state.accountDestination}/>
    </>
  );
}
